/**
 * #1800 — the one way to read a server's error message in a UI handler.
 *
 * apiClient (services/api/client.ts) rejects with the response BODY, with the
 * HTTP `status` and `code` stamped on it: `{ success: false, error, code,
 * details, status }`. Handlers that read `err.response.data.error` (the raw
 * axios shape) never found anything there and always showed their generic
 * fallback, so the server's reason for refusing was dropped.
 *
 * This reads, in order:
 *   1. `err.error`, the apiClient body (a string, or `{ message }`);
 *   2. `err.response.data.error` / `.message`, for the few callers that still
 *      use a bare axios instance or fetch wrapper that rejects with an
 *      AxiosError;
 *   3. `err.message`: the body's own `message`, or a thrown Error's.
 *
 * An AxiosError with no server message (network down, timeout, CORS, an empty
 * error body) only has axios's own English text ("Network Error", "Request
 * failed with status code 500"), so it gets the caller's (translated) fallback.
 */

import i18n from 'i18next'

/**
 * #2087: a refusal at a paused hotel. Controllers send
 * `{ error: <message>, code: 'hotel_paused' }`; the global error handler sends
 * `{ error: 'hotel_paused', code: 'hotel_paused', message }`.
 */
export const HOTEL_PAUSED_CODE = 'hotel_paused'

export function isHotelPausedError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as { code?: unknown; error?: unknown }
  return e.code === HOTEL_PAUSED_CODE || e.error === HOTEL_PAUSED_CODE
}

/**
 * "This hotel is paused", in the viewer's language. Reads the i18next
 * singleton the app initialises at startup (importing @/i18n/config here would
 * re-initialise it).
 */
export function hotelPausedMessage(): string {
  return i18n.t('common:errors.hotelPaused', { defaultValue: 'This hotel is paused' })
}

/**
 * #2287 part 5 (spec §12): the same refusal at a Coming Soon hotel ('to do Live
 * tests'), code 'hotel_coming_soon'. apiClient does not toast a 409, so every
 * screen that starts an affiliation shows it itself (hotelClosedOr /
 * apiErrorMessage).
 */
export const HOTEL_COMING_SOON_CODE = 'hotel_coming_soon'

export function isHotelComingSoonError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false
  const e = err as { code?: unknown; error?: unknown }
  return e.code === HOTEL_COMING_SOON_CODE || e.error === HOTEL_COMING_SOON_CODE
}

export function hotelComingSoonMessage(): string {
  return i18n.t('common:errors.hotelComingSoon', {
    defaultValue: 'This hotel is Coming Soon. It opens for new partners once its integration tests are approved.',
  })
}

/** The message for a refusal at a paused or Coming Soon hotel, or undefined for any other error. */
export function hotelClosedMessage(err: unknown): string | undefined {
  if (isHotelPausedError(err)) return hotelPausedMessage()
  if (isHotelComingSoonError(err)) return hotelComingSoonMessage()
  return undefined
}

/** The paused or Coming Soon message for that refusal, otherwise the caller's own. */
export function hotelClosedOr(err: unknown, fallback: string): string {
  return hotelClosedMessage(err) ?? fallback
}

/**
 * #2208: true when apiClient already showed a toast for this rejection, so a
 * handler that toasts too would show the error twice. Reads the `toasted`
 * flag services/api/client.ts stamps from handleErrorResponse's own result,
 * so the two cannot drift apart.
 */
export function apiClientToasted(err: unknown): boolean {
  return !!err && typeof err === 'object' && (err as { toasted?: unknown }).toasted === true
}

// A proxy's HTML error page (a 502 from nginx) reaches apiClient as a string
// body, which it turns into `{ message: '<html>…' }`; never show markup.
const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() && !/^\s*</.test(value) ? value : undefined

const fromErrorField = (value: unknown): string | undefined => {
  if (value && typeof value === 'object') return text((value as { message?: unknown }).message)
  return text(value)
}

export function apiErrorMessage(err: unknown): string | undefined
export function apiErrorMessage(err: unknown, fallback: string): string
export function apiErrorMessage(err: unknown, fallback?: string): string | undefined {
  if (typeof err === 'string') return text(err) ?? fallback
  if (!err || typeof err !== 'object') return fallback
  const closed = hotelClosedMessage(err)
  if (closed) return closed

  const e = err as {
    error?: unknown
    message?: unknown
    isAxiosError?: boolean
    response?: { data?: { error?: unknown; message?: unknown } | string }
  }

  const bodyError = fromErrorField(e.error)
  if (bodyError) return bodyError

  const data = e.response?.data
  if (data && typeof data === 'object') {
    const axiosMessage = fromErrorField(data.error) ?? text(data.message)
    if (axiosMessage) return axiosMessage
  } else if (text(data)) {
    return text(data)
  }

  // An AxiosError's own message is axios's English text ("Network Error",
  // "Request failed with status code 500"), never the server's reason.
  if (e.isAxiosError && fallback !== undefined) return fallback

  return text(e.message) ?? fallback
}
